import React, { useState, useMemo, useCallback, createContext } from 'react';
import { DataTableColumn } from '../DataTable';
import {
  getStickyColumnStyle,
  CELL_PADDING,
  CELL_EDGE_PADDING,
} from '../DataTable/DataTable.utils';

const MIN_COLUMN_WIDTH = 60;

interface ColumnWidthMap {
  [columnId: string]: number;
}

type ColumnWidth = DataTableColumn['width'];

export interface ColumnResizeContextValue {
  columnWidths: ColumnWidthMap | null;
  getEffectiveColumnWidth: (column: DataTableColumn) => ColumnWidth;
  getStickyColumnStyle: (
    columns: DataTableColumn[],
    column: DataTableColumn,
  ) => { left: number };
  getTableWidth: (fallback?: string) => string;
  resizeColumn: (columnId: string, newWidth: number) => void;
  startColumnResize?: (columnId: string, e: MouseEvent) => void;
  endColumnResize?: (columnId: string, e: MouseEvent) => void;
}

export interface ColumnResizeProps {
  columns?: DataTableColumn[];
  isTableResizable?: boolean;
  onColumnResize?: (columnId: string, newWidth: number) => void;
  onColumnResizeStart?: (
    columnId: string,
    startWidth: number,
    e: MouseEvent,
  ) => void;
  onColumnResizeEnd?: (
    columnId: string,
    endWidth: number,
    e: MouseEvent,
  ) => void;
  minColumnWidth?: number;
  maxColumnWidth?: number;
  children?: React.ReactNode;
}

const parseColumnWidth = (width?: string | number): number | string => {
  if (!width) return MIN_COLUMN_WIDTH;

  if (typeof width === 'number') {
    return width;
  }

  if (typeof width === 'string') {
    if (width.endsWith('px')) {
      const parsed = parseInt(width, 10);
      return isNaN(parsed) ? MIN_COLUMN_WIDTH : parsed;
    }
    if (width.endsWith('%')) {
      return width;
    }
    const parsed = parseInt(width, 10);
    return isNaN(parsed) ? MIN_COLUMN_WIDTH : parsed;
  }

  return MIN_COLUMN_WIDTH;
};

const createDefaultContext = (): ColumnResizeContextValue => ({
  columnWidths: null,
  getEffectiveColumnWidth: (column: DataTableColumn) => column.width,
  // default handling of sticky columns style is the exsisting util
  getStickyColumnStyle: (
    columns: DataTableColumn[],
    column: DataTableColumn,
  ) => {
    return getStickyColumnStyle(columns, column);
  },
  getTableWidth: (fallback?: string) => fallback || '100%',
  resizeColumn: () => {},
});

export const ColumnResizeContext = createContext<ColumnResizeContextValue>(createDefaultContext());

/**
 * ColumnResize manages the state and logic of column resizing for tables.
 * Given an array of columns with resize configuration, it provides a context with resize handlers,
 * width calculations, and sticky column positioning.
 *
 * Key features:
 * - Manages column width state with min/max constraints
 * - Provides effective width calculations for columns
 * - Handles sticky column positioning with dynamic widths
 * - Supports both resizable and non-resizable table modes
 * - Offers resize event callbacks (onColumnResize, onColumnResizeStart, onColumnResizeEnd)
 */
export const ColumnResize: React.FC<ColumnResizeProps> = ({
  columns = [],
  isTableResizable = false,
  onColumnResize,
  onColumnResizeStart,
  onColumnResizeEnd,
  minColumnWidth = MIN_COLUMN_WIDTH,
  maxColumnWidth,
  children,
}) => {
  const initializeColumnWidths = (): ColumnWidthMap | null => {
    if (!isTableResizable) {
      return null;
    }

    // Resizable flow: Parse all widths to numbers, store in mapping
    return columns.reduce((acc: ColumnWidthMap, column) => {
      const id = column.resizeProps?.id;
      if (id) {
        const parsedWidth = parseColumnWidth(column.width);
        if (typeof parsedWidth === 'number') {
          acc[id] = parsedWidth;
        }
      }
      return acc;
    }, {});
  };

  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap | null>(initializeColumnWidths);

  const getEffectiveColumnWidth = useCallback(
    (column: DataTableColumn): ColumnWidth => {
      if (isTableResizable && columnWidths) {
        const id = column.resizeProps?.id?.toString();

        if (id && columnWidths[id]) {
          return columnWidths[id];
        }

        return parseColumnWidth(column.width);
      }
      return column.width;
    },
    [isTableResizable, columnWidths],
  );

  const getStickyColumnStyle = useCallback(
    (columns: DataTableColumn[], column: DataTableColumn) => {
      let left = 0;

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        if (col === column) {
          break;
        }

        const horizontalPadding =
          i === 0 ? CELL_EDGE_PADDING + CELL_PADDING : 2 * CELL_PADDING;

        const effectiveWidth = getEffectiveColumnWidth(col);
        const widthValue =
          typeof effectiveWidth === 'string'
            ? parseInt(effectiveWidth, 10)
            : effectiveWidth;

        left += (widthValue ?? MIN_COLUMN_WIDTH) + horizontalPadding;
      }

      return { left };
    },
    [getEffectiveColumnWidth],
  );

  const handleColumnResize = useCallback(
    (columnId: string, newWidth: number): void => {
      if (!isTableResizable || !columnWidths) {
        return;
      }

      let clampedWidth = newWidth;
      if (minColumnWidth !== undefined && newWidth < minColumnWidth) {
        clampedWidth = minColumnWidth;
      }
      if (maxColumnWidth !== undefined && newWidth > maxColumnWidth) {
        clampedWidth = maxColumnWidth;
      }

      setColumnWidths(prevState => {
        if (!prevState) {
          return prevState;
        }

        const updatedColumnWidths = {
          ...prevState,
          [columnId]: clampedWidth,
        };

        return updatedColumnWidths;
      });

      if (onColumnResize) {
        onColumnResize(columnId, clampedWidth);
      }
    },
    [
      isTableResizable,
      columnWidths,
      minColumnWidth,
      maxColumnWidth,
      onColumnResize,
    ],
  );

  const handleColumnResizeStart = useCallback(
    (columnId: string, e: MouseEvent): void => {
      if (onColumnResizeStart && columnWidths) {
        onColumnResizeStart(columnId, columnWidths[columnId], e);
      }
    },
    [onColumnResizeStart, columnWidths],
  );

  const handleColumnResizeEnd = useCallback(
    (columnId: string, e: MouseEvent): void => {
      if (onColumnResizeEnd && columnWidths) {
        onColumnResizeEnd(columnId, columnWidths[columnId], e);
      }
    },
    [onColumnResizeEnd, columnWidths],
  );

  const getTableWidth = useCallback(
    (fallbackWidth?: string): string => {
      if (!isTableResizable) {
        return fallbackWidth || '100%';
      }

      return 'fit-content';
    },
    [isTableResizable],
  );

  const contextValue = useMemo<ColumnResizeContextValue>(
    () => ({
      columnWidths,
      getEffectiveColumnWidth,
      getStickyColumnStyle,
      getTableWidth,
      resizeColumn: handleColumnResize,
      startColumnResize: handleColumnResizeStart,
      endColumnResize: handleColumnResizeEnd,
    }),
    [
      columnWidths,
      getEffectiveColumnWidth,
      getStickyColumnStyle,
      getTableWidth,
      handleColumnResize,
      handleColumnResizeStart,
      handleColumnResizeEnd,
    ],
  );

  return (
    <ColumnResizeContext.Provider value={contextValue}>
      {children}
    </ColumnResizeContext.Provider>
  );
};
